package com.macrofocus.eflux.ui.morphing

import org.locationtech.jts.geom.Coordinate
import org.locationtech.jts.geom.GeometryFactory
import org.locationtech.jts.geom.Polygon
import kotlin.math.pow
import kotlin.math.sqrt

fun align(referencePolygon: Polygon, shiftingPolygon: Polygon) : Polygon {
    val shift = findBestVertexShift(referencePolygon, shiftingPolygon)
    val polygon = createShiftedPolygon(shiftingPolygon, shift)
    return polygon
}

private fun findBestVertexShift(
    referencePolygon: Polygon,
    shiftingPolygon: Polygon
): Int {
    // Validate input polygons and ensure they have same number of vertices
//    if (!referencePolygon.isValid || !shiftingPolygon.isValid) {
//        throw IllegalArgumentException("Invalid polygon geometry")
//    }

    val refCoords = referencePolygon.coordinates
    val shiftCoords = shiftingPolygon.coordinates

    if (refCoords.size != shiftCoords.size) {
        throw IllegalArgumentException("Polygons must have same number of vertices")
    }

    val n = refCoords.size - 1 // Subtract 1 because first and last are same in closed polygon
    if (n < 3) {
        throw IllegalArgumentException("Polygons must have at least 3 distinct vertices")
    }

    var bestShift = 0
    var minDistanceSum = Double.MAX_VALUE

    // Try each possible shift
    for (shift in 0 until n) {
        val shiftedCoords = shiftCoordinates(shiftCoords, shift)
        val distanceSum = calculateTotalDistance(refCoords, shiftedCoords)

        if (distanceSum < minDistanceSum) {
            minDistanceSum = distanceSum
            bestShift = shift
        }
    }

    return bestShift
}

/**
 * Shift the coordinate array by moving the starting point
 */
private fun shiftCoordinates(coords: Array<Coordinate>, shift: Int): Array<Coordinate> {
    val n = coords.size - 1 // Actual number of unique vertices
    val shifted = Array(coords.size) { Coordinate() }

    // Copy coordinates with shift, maintaining closure
    for (i in 0 until n) {
        shifted[i] = coords[(i + shift) % n]
    }
    shifted[n] = shifted[0] // Close the polygon

    return shifted
}

/**
 * Calculate total Euclidean distance between corresponding points
 */
private fun calculateTotalDistance(coords1: Array<Coordinate>, coords2: Array<Coordinate>): Double {
    return coords1.zip(coords2) { c1, c2 ->
        sqrt((c1.x - c2.x).pow(2) + (c1.y - c2.y).pow(2))
    }.sum()
}

// Example usage with helper function to create shifted polygon
fun createShiftedPolygon(original: Polygon, shift: Int): Polygon {
    val factory = GeometryFactory()
    val shiftedCoords = shiftCoordinates(original.coordinates, shift)
    return factory.createPolygon(shiftedCoords)
}

fun main() {
    val factory = GeometryFactory()

    // Create a reference square
    val refCoords = arrayOf(
        Coordinate(0.0, 0.0),
        Coordinate(1.0, 0.0),
        Coordinate(1.0, 1.0),
        Coordinate(0.0, 1.0),
        Coordinate(0.0, 0.0)
    )
    val refPolygon = factory.createPolygon(refCoords)

    // Create a square with different vertex order
    val shiftCoords = arrayOf(
        Coordinate(1.1, 1.1),  // Slightly offset to show distance difference
        Coordinate(0.1, 1.1),
        Coordinate(0.1, 0.1),
        Coordinate(1.1, 0.1),
        Coordinate(1.1, 1.1)
    )
    val shiftPolygon = factory.createPolygon(shiftCoords)

    try {
        val bestShift = findBestVertexShift(refPolygon, shiftPolygon)
        println("Best vertex shift: $bestShift")

        val shiftedPolygon = createShiftedPolygon(shiftPolygon, bestShift)
        println("Original coordinates: ${shiftPolygon.coordinates.joinToString()}")
        println("Shifted coordinates: ${shiftedPolygon.coordinates.joinToString()}")
    } catch (e: IllegalArgumentException) {
        println("Error: ${e.message}")
    }
}